import React from "react";
import { DatePicker, Select } from "antd";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import {
  setStartAndEndDate,
  setIsAutoCompareDates,
  setDefaultsFetchedForDropdown,
  setDateRangeManuallyUpdated,
} from "../../redux/store/genericReportingSlice";
import { useGetDropdownsQuery } from "../../redux/api/dropdowns";
import { useEffect } from "react";

const DateRangePicker = ({
  style = { width: "16vw" },
  maxDate = dayjs().subtract(1, "day"),
  onOpenChange = null
}) => {
  const dispatch = useDispatch();
  const selectedStartDate = useSelector(
    (state) => state.genericReporting.selectedDropdownValues['start_date']
  );
  const selectedEndDate = useSelector(
    (state) => state.genericReporting.selectedDropdownValues['end_date']
  );
  const defaultsFetchedForDropdown = useSelector(
    (state) => state.genericReporting.defaultsFetchedForDropdown
  )
  const currentDropdownGroup = useSelector(
    (state) => state.genericReporting.currentDropdownGroup
  )

  const { data: dateRangeOptions, isLoading, isError, isFetching } = useGetDropdownsQuery({ 
    data: { 
      dropdown_name: 'date_range',
      dropdown_group: currentDropdownGroup
    },
  });

  useEffect(() => {
    if (dateRangeOptions?.default && !defaultsFetchedForDropdown[currentDropdownGroup]?.['date_range']) {
      // Handle the default dates - they might already be formatted strings
      const startDate = dateRangeOptions.default[0];
      const endDate = dateRangeOptions.default[1];

      console.log("startDate", startDate);
      console.log("endDate", endDate);
      
      // If they're already strings, use them directly; otherwise format them
      const formattedStartDate = typeof startDate === 'string' ? startDate : dayjs(startDate).format("YYYY-MM-DD");
      const formattedEndDate = typeof endDate === 'string' ? endDate : dayjs(endDate).format("YYYY-MM-DD");
      
      dispatch(
        setStartAndEndDate([formattedStartDate, formattedEndDate])
      );
      dispatch(setDefaultsFetchedForDropdown({
        dropdownName: 'date_range',
        value: true
      }));
    }
  }, [dateRangeOptions]);

  // Helper function to get quarter start and end dates
  const getQuarterDates = (date) => {
    const month = date.month();
    const year = date.year();
    let quarterStartMonth;
    
    if (month < 3) {
      quarterStartMonth = 0; // Q1: Jan-Mar
    } else if (month < 6) {
      quarterStartMonth = 3; // Q2: Apr-Jun
    } else if (month < 9) {
      quarterStartMonth = 6; // Q3: Jul-Sep
    } else {
      quarterStartMonth = 9; // Q4: Oct-Dec
    }
    
    const quarterStart = dayjs().year(year).month(quarterStartMonth).startOf('month');
    const quarterEnd = quarterStart.add(2, 'month').endOf('month');
    
    return { quarterStart, quarterEnd };
  };

  // Helper function to get the last quarter dates
  const getLastQuarterDates = () => {
    const threeMonthsAgo = dayjs().subtract(3, "month");
    const { quarterStart, quarterEnd } = getQuarterDates(threeMonthsAgo);
    return { quarterStart, quarterEnd };
  };

  const rangePresets = dateRangeOptions?.presets || [
    {
      label: "Last 7 days",
      value: [dayjs().add(-7, "d"), dayjs().add(-1, "d")],
    },
    {
      label: "Last week",
      value: [
        dayjs().subtract(1, "week").startOf("week"),
        dayjs().subtract(1, "week").endOf("week"),
      ],
    },
    {
      label: "Last 30 days",
      value: [dayjs().add(-30, "d"), dayjs().add(-1, "d")],
    },
    {
      label: "Last month",
      value: [
        dayjs().subtract(1, "month").startOf("month"),
        dayjs().subtract(1, "month").endOf("month"),
      ],
    },
    {
      label: "Last quarter",
      value: (() => {
        const { quarterStart, quarterEnd } = getLastQuarterDates();
        return [quarterStart, quarterEnd];
      })(),
    },
    {
      label: "Last 90 days",
      value: [dayjs().add(-90, "d"), dayjs().add(-1, "d")],
    },
    {
      label: "Month to date",
      value: [dayjs().startOf("month"), dayjs().add(-1, "d")],
    },
    {
      label: "Year to date",
      value: [dayjs().startOf("year"), dayjs().add(-1, "d")],
    },
  ];

  const handleDateChange = (dates, dateStrings) => {
    dispatch(setIsAutoCompareDates(true));
    dispatch(setDefaultsFetchedForDropdown({
      dropdownName: 'compare_date_range',
      value: false
    }));
    dispatch(
      setStartAndEndDate([dateStrings[0], dateStrings[1]])
    );
    if (onOpenChange) {
      onOpenChange(false);
    }
    dispatch(setDateRangeManuallyUpdated(true));
  };

  // Safely convert dates to dayjs objects
  const getDateValue = (date) => {
    if (!date) return null;
    const dayjsDate = dayjs(date);
    return dayjsDate.isValid() ? dayjsDate : null;
  };

  if (isLoading || isFetching) {
    return <Select options={[]} value={[]} onChange={() => {}} loading={true} style={style} />
  }

  return (
    <DatePicker.RangePicker
      style={style}
      presets={rangePresets}
      value={[getDateValue(selectedStartDate), getDateValue(selectedEndDate)]}
      format="YYYY-MM-DD"
      onChange={handleDateChange}
      maxDate={maxDate}
    />
  );
};

export default DateRangePicker;
